import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  /** First item index (0-based) for "Showing X to Y of Z" */
  startIndex: number;
  /** Last item index (exclusive) for "Showing X to Y of Z" */
  endIndex: number;
  /** Total count of items being paginated */
  total: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (perPage: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  startIndex,
  endIndex,
  total,
  loading,
  onPageChange,
  onItemsPerPageChange,
}) => {
  if (loading || total === 0) return null;

  return (
    <div className="px-2 py-2 theme-border-t flex flex-col sm:flex-row items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="text-sm theme-text-secondary">
          Showing {startIndex + 1} to {Math.min(endIndex, total)} of {total} results
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-1.5 rounded theme-border disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 transition-colors theme-text-primary"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="px-2 py-1 text-sm theme-text-secondary">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-1.5 rounded theme-border disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 transition-colors theme-text-primary"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
